import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GPUUtilizationChart from './GPUUtilizationChart';
import GPUMemoryGauge from './GPUMemoryGauge';
import GPUTemperature from './GPUTemperature';
import GPUProcessList from './GPUProcessList';
import InstanceCostMeter from './InstanceCostMeter';
import AICostMeter from './AICostMeter';
import { getWebSocketUrl } from '../../../utils/api';

interface GPUMetrics {
  timestamp: string;
  gpu: {
    utilization: number;
    memory_used: number;
    memory_total: number;
    temperature: number;
    power_draw: number;
    name: string;
  };
  processes?: Array<{
    pid: number;
    name: string;
    memory: number;
  }>;
}

interface GPUMetricsHeaderProps {
  instanceId: string;
  instanceType?: string;
  hourlyCost?: number;
  provider?: string;
  region?: string;
  launchTime?: string | null;
  aiSessionCostUsd?: number;
  aiInputTokens?: number;
  aiOutputTokens?: number;
}

const GPUMetricsHeader: React.FC<GPUMetricsHeaderProps> = ({
  instanceId,
  instanceType,
  hourlyCost = 0,
  provider = 'aws',
  region = 'us-east-1',
  launchTime,
  aiSessionCostUsd = 0,
  aiInputTokens = 0,
  aiOutputTokens = 0,
}) => {
  const [metrics, setMetrics] = useState<GPUMetrics | null>(null);
  const [history, setHistory] = useState<GPUMetrics[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stopReconnectRef = useRef(false);

  useEffect(() => {
    console.log('GPUMetricsHeader: instanceId changed to:', instanceId);
    if (!instanceId) {
      console.log('GPUMetricsHeader: No instanceId, skipping WebSocket connection');
      return;
    }

    stopReconnectRef.current = false;

    const connectWebSocket = () => {
      if (stopReconnectRef.current) return;

      try {
        // Get auth token
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('GPUMetricsHeader: No auth token found');
          setError('Authentication required');
          return;
        }

        // Create WebSocket connection
        const wsUrl = getWebSocketUrl(`/api/v1/notebooks/gpu-metrics/${instanceId}`);
        console.log('GPUMetricsHeader: Connecting to WebSocket URL:', wsUrl);

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('GPU metrics WebSocket connected successfully');
          setIsConnected(true);
          setError(null);

          // Send authentication
          console.log('GPUMetricsHeader: Sending auth token');
          ws.send(JSON.stringify({
            type: 'auth',
            token: token
          }));
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'metrics') {
              setMetrics(data);
              setHistory(prev => {
                const newHistory = [...prev, data];
                // Keep last 60 seconds of data
                if (newHistory.length > 60) {
                  return newHistory.slice(-60);
                }
                return newHistory;
              });
            } else if (data.type === 'error') {
              const msg = data.message || '';
              setError(msg);
              // Only stop reconnecting for terminal errors — transient failures should retry
              const isTerminal = ['auth', 'not found', 'not running', 'no gpu detected', 'unauthorized', 'no instance'].some(
                term => msg.toLowerCase().includes(term)
              );
              if (isTerminal) {
                stopReconnectRef.current = true;
              }
              setIsConnected(false);
            } else if (data.type === 'status') {
              console.log('GPU metrics status:', data.message);
            }
          } catch (e) {
            console.error('Failed to parse GPU metrics:', e);
          }
        };

        ws.onerror = (error) => {
          console.error('GPU metrics WebSocket error:', error);
          setIsConnected(false);
        };

        ws.onclose = () => {
          setIsConnected(false);
          wsRef.current = null;

          // Reconnect after 5 seconds unless a terminal error was received
          if (!stopReconnectRef.current) {
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
          }
        };

      } catch (e) {
        console.error('Failed to connect GPU metrics WebSocket:', e);
        setError('Failed to connect');
        setIsConnected(false);
      }
    };

    connectWebSocket();

    // Cleanup
    return () => {
      stopReconnectRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error?.includes('No GPU detected')) {
    return null; // Don't show GPU metrics for non-GPU instances
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-black/50 backdrop-blur-sm border-b border-gray-800/50 shadow-lg"
    >
      {/* Header Bar with glassy style - more compact */}
      <div className="px-4 py-2 flex items-center justify-between bg-black/30">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className={`h-2 w-2 ${isConnected ? 'bg-green-500/80' : 'bg-red-500/80'} animate-pulse shadow-lg ${isConnected ? 'shadow-green-500/50' : 'shadow-red-500/50'}`} />
            <span className="text-sm font-medium text-white">
              {metrics?.gpu.name || 'GPU Metrics'}
            </span>
          </div>
          
          {instanceType && (
            <span className="text-xs text-gray-400 font-mono">
              {instanceType}
            </span>
          )}
          
          {provider && region && (
            <span className="text-xs text-gray-500">
              {provider.toUpperCase()} • {region}
            </span>
          )}
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <svg
            className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Metrics Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-2">
              {error && !error.includes('No GPU detected') && (
                <div className="mb-2 p-2 bg-red-500/10 border border-red-500/30 backdrop-blur-sm">
                  <p className="text-xs text-red-400">{error}</p>
                </div>
              )}

              {!isConnected && !error && (
                <div className="mb-2 p-2 bg-yellow-500/10 border border-yellow-500/30 backdrop-blur-sm">
                  <p className="text-xs text-yellow-400">Connecting to GPU metrics...</p>
                </div>
              )}

              {metrics && (
                <div className="grid grid-cols-12 gap-2">
                  {/* Utilization Chart */}
                  <div className={aiInputTokens + aiOutputTokens > 0 ? 'col-span-3' : 'col-span-4'}>
                    <GPUUtilizationChart
                      history={history}
                      currentUtilization={metrics.gpu.utilization}
                    />
                  </div>

                  {/* Memory Gauge */}
                  <div className="col-span-2">
                    <GPUMemoryGauge
                      used={metrics.gpu.memory_used}
                      total={metrics.gpu.memory_total}
                    />
                  </div>

                  {/* Temperature */}
                  <div className="col-span-2">
                    <GPUTemperature
                      temperature={metrics.gpu.temperature}
                      powerDraw={metrics.gpu.power_draw}
                    />
                  </div>

                  {/* Process List */}
                  <div className="col-span-2">
                    <GPUProcessList
                      processes={metrics.processes || []}
                    />
                  </div>

                  {/* Instance Cost Meter */}
                  <div className="col-span-2">
                    <InstanceCostMeter
                      hourlyCost={hourlyCost}
                      launchTime={launchTime}
                    />
                  </div>

                  {/* AI Cost Meter - Only shows when AI has been used */}
                  {aiInputTokens + aiOutputTokens > 0 && (
                    <div className="col-span-1">
                      <AICostMeter
                        sessionCostUsd={aiSessionCostUsd}
                        inputTokens={aiInputTokens}
                        outputTokens={aiOutputTokens}
                      />
                    </div>
                  )}
                </div>
              )}

              {!metrics && isConnected && (
                <div className="h-20 flex items-center justify-center">
                  <div className="text-gray-500 text-xs">Waiting for GPU metrics...</div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default GPUMetricsHeader;